import { useEffect, useState, useCallback, useRef } from "react";
import { XCircle, Scissors, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, PnL, money, fmt, EmptyState } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const POLL_MS = 5000;

export default function Positions() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState("");
  const [partial, setPartial] = useState(null); // {position, volume}
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/investor/positions");
      setData(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch live positions");
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  const closeOne = async (p) => {
    if (!window.confirm(`Close ${p.type.toUpperCase()} ${p.volume} ${p.symbol} (ticket ${p.id}) at market?`)) return;
    setBusy(p.id);
    try {
      await api.post(`/investor/positions/${p.id}/close`);
      toast.success(`Position ${p.id} closed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Close failed"); }
    finally { setBusy(""); }
  };

  const closeAll = async () => {
    if (!window.confirm("Close ALL open positions at market price?")) return;
    setBusy("all");
    try {
      const { data: res } = await api.post("/investor/positions/close-all");
      toast.success(`${res.closed} position(s) closed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Close all failed"); }
    finally { setBusy(""); }
  };

  const submitPartial = async () => {
    const vol = parseFloat(partial.volume);
    const maxVol = Math.max(0, partial.position.volume - 0.01);
    if (maxVol <= 0) return toast.error("Position too small for partial close — use Close instead");
    if (!vol || vol <= 0 || vol > maxVol) {
      return toast.error(`Volume must be between 0.01 and ${maxVol.toFixed(2)}`);
    }
    setBusy(partial.position.id);
    try {
      await api.post(`/investor/positions/${partial.position.id}/partial-close`, { volume: vol });
      toast.success(`Closed ${vol} lots of ${partial.position.symbol}`);
      setPartial(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Partial close failed"); }
    finally { setBusy(""); }
  };

  if (!data && !error) return <div className="space-y-4"><div className="skeleton h-20" /><div className="skeleton h-72" /></div>;

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Open Positions" sub="Live from MetaApi" />
        <div className="card-dark border-[#FF3B30]/40 p-6" data-testid="positions-error">
          <p className="text-sm text-[#FF3B30] break-all">{error}</p>
          <button data-testid="positions-retry-button" onClick={load} className="btn-outline px-4 py-2 mt-4 text-sm flex items-center gap-2"><RefreshCw size={14} /> Retry</button>
        </div>
      </div>
    );
  }

  if (!data.connected) {
    return (
      <div>
        <PageHeader title="Open Positions" sub="Live from MetaApi" />
        <EmptyState title="No MT5 account connected" sub="Connect your broker account to see live positions." />
      </div>
    );
  }

  const totalPnl = data.positions.reduce((s, p) => s + (p.profit || 0), 0);

  return (
    <div>
      <PageHeader title="Open Positions" sub={`Live from MetaApi · refreshes every ${POLL_MS / 1000}s`}>
        {data.positions.length > 0 && (
          <button data-testid="close-all-positions-button" onClick={closeAll} disabled={busy === "all"}
            className="btn-outline px-5 py-2 text-sm flex items-center gap-2 text-[#FF3B30] disabled:opacity-50">
            <XCircle size={15} /> {busy === "all" ? "Closing…" : "Close All"}
          </button>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[["Balance", data.account?.balance != null ? money(data.account.balance) : "—", "text-white"],
          ["Equity", data.account?.equity != null ? money(data.account.equity) : "—", "text-[#00F0FF]"],
          ["Open Positions", data.positions.length, "text-white"],
          ["Floating P&L", <PnL key="pnl" value={totalPnl} />, ""]].map(([l, v, c]) => (
          <div key={l} className="card-dark p-4" data-testid={`positions-stat-${String(l).toLowerCase().replace(/\s|&/g, "-")}`}>
            <p className="label-xs">{l}</p>
            <p className={`tabular text-lg mt-1 ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      {error && <p className="text-xs text-[#FFD600] mb-3" data-testid="positions-stale-warning">Live refresh error: {String(error).slice(0, 140)} — showing last data</p>}

      {data.positions.length === 0 ? (
        <EmptyState title="No open positions" sub="Live positions appear here the moment a trade opens on your MT5 account." />
      ) : (
        <Table headers={["Symbol", "Ticket ID", "Type", "Lots", "Open Price", "Current", "SL", "TP", "P&L", "Actions"]} testId="positions-table">
          {data.positions.map((p) => (
            <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`position-row-${p.id}`}>
              <Td className="tabular text-white">{p.symbol}</Td>
              <Td className="tabular text-xs text-gray-500">{p.id}</Td>
              <Td><span className={p.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{p.type.toUpperCase()}</span></Td>
              <Td className="tabular">{fmt(p.volume)}</Td>
              <Td className="tabular">{p.open_price}</Td>
              <Td className="tabular text-gray-400">{p.current_price ?? "—"}</Td>
              <Td className="tabular text-xs text-[#FF3B30]">{p.sl ?? "—"}</Td>
              <Td className="tabular text-xs text-[#00E676]">{p.tp ?? "—"}</Td>
              <Td><PnL value={p.profit} testId={`position-pnl-${p.id}`} /></Td>
              <Td>
                <div className="flex gap-2">
                  <button data-testid={`close-position-${p.id}`} onClick={() => closeOne(p)} disabled={busy === p.id}
                    className="btn-outline px-3 py-1 text-xs text-[#FF3B30] disabled:opacity-50">Close</button>
                  <button data-testid={`partial-close-${p.id}`} onClick={() => setPartial({ position: p, volume: "" })}
                    className="btn-outline px-3 py-1 text-xs text-[#FFD600] flex items-center gap-1"><Scissors size={11} /> Partial</button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={!!partial} onOpenChange={(o) => !o && setPartial(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-white">Partial Close</DialogTitle></DialogHeader>
          {partial && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">{partial.position.type.toUpperCase()} {partial.position.volume} {partial.position.symbol} · ticket {partial.position.id}</p>
              <div>
                <label className="label-xs block mb-2">Volume to close (max {(partial.position.volume - 0.01).toFixed(2)})</label>
                <input data-testid="partial-volume-input" type="number" step="0.01" min="0.01" max={partial.position.volume - 0.01}
                  className="input-dark" placeholder="e.g. 0.05" value={partial.volume}
                  onChange={(e) => setPartial({ ...partial, volume: e.target.value })} />
              </div>
              <button data-testid="partial-submit-button" onClick={submitPartial} disabled={busy === partial.position.id}
                className="btn-primary w-full py-2.5 text-sm disabled:opacity-50">Close Partial Volume</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
